import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Chip, { LEGACY_CHIP_VARIANT } from '../Chip';
import Icon from '../Icon';
import { useTranslations } from '../../context/TranslationContext';
import {
    SUGGESTION_CHIPS_ICON,
    SUGGESTION_CHIPS_VARIANT,
    SUGGESTION_CHIPS_VARIANTS,
} from './constants';
import { normalizeSuggestions, toIdSet } from './helpers';

/**
 * A labelled row of tappable suggestion chips ("Suggestions: Data analysis +, SQL +").
 * Picking one calls `onSelect(item)`; ids in `loadingIds` show a spinner and ignore clicks.
 * `variant="outline"` is the solid-bordered chip with the add sign leading, for pickers that
 * list what can be added. Renders nothing when there are no suggestions.
 */
const SuggestionChips = ({
    items = [],
    onSelect = () => {},
    label,
    variant = SUGGESTION_CHIPS_VARIANT.DASHED,
    disabled = false,
    loadingIds,
    labels = {},
    className,
    ...rest
}) => {
    const { t } = useTranslations();
    const suggestions = normalizeSuggestions(items);
    if (!suggestions.length) return null;

    const loading = toIdSet(loadingIds);
    const savingLabel = labels.saving || t('Saving...');

    return (
        <div
            className={classNames(
                'ds-suggestion-chips',
                `ds-suggestion-chips--${SUGGESTION_CHIPS_VARIANTS.includes(variant) ? variant : SUGGESTION_CHIPS_VARIANT.DASHED}`,
                { 'ds-suggestion-chips--disabled': disabled },
                className,
            )}
            {...rest}
        >
            {label && <span className="ds-suggestion-chips__label">{label}</span>}
            <ul className="ds-suggestion-chips__list" role="list">
                {suggestions.map((item) => {
                    const saving = loading.has(item.id);
                    const inert = disabled || saving;
                    const outline = variant === SUGGESTION_CHIPS_VARIANT.OUTLINE;
                    return (
                        <li key={item.id} className="ds-suggestion-chips__item">
                            <Chip
                                variant={outline ? LEGACY_CHIP_VARIANT.DEFAULT : LEGACY_CHIP_VARIANT.DASHED}
                                className="ds-suggestion-chips__chip"
                                disabled={inert}
                                showLoader={saving}
                                onClick={() => !inert && onSelect(item)}
                                value={
                                    saving ? savingLabel : (
                                        <>
                                            {outline && (
                                                <Icon value={SUGGESTION_CHIPS_ICON.ADD} className="chip-action" noMargin />
                                            )}
                                            {item.label}
                                            {!outline && (
                                                <Icon value={SUGGESTION_CHIPS_ICON.ADD} className="chip-action" noMargin />
                                            )}
                                        </>
                                    )
                                }
                            />
                        </li>
                    );
                })}
            </ul>
        </div>
    );
};

SuggestionChips.propTypes = {
    /** Suggestions: strings or `{ id, label }` */
    items: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.shape({ id: PropTypes.any, label: PropTypes.string })])),
    /** Called with the normalized `{ id, label }` when a chip is picked */
    onSelect: PropTypes.func,
    /** Leading label, e.g. "Suggestions" */
    label: PropTypes.node,
    /** `dashed` (default) is the hint chip; `outline` is the solid add-chip */
    variant: PropTypes.oneOf(SUGGESTION_CHIPS_VARIANTS),
    /** Disable every chip */
    disabled: PropTypes.bool,
    /** Ids currently being added: shown with a spinner, clicks ignored */
    loadingIds: PropTypes.oneOfType([PropTypes.instanceOf(Set), PropTypes.arrayOf(PropTypes.string), PropTypes.object]),
    /** Translated texts: `{ saving }` */
    labels: PropTypes.shape({ saving: PropTypes.string }),
    /** Extra class on the wrapper */
    className: PropTypes.string,
};

export { normalizeSuggestions, toIdSet, SUGGESTION_CHIPS_ICON };
export { SUGGESTION_CHIPS_VARIANT, SUGGESTION_CHIPS_VARIANTS };
export default SuggestionChips;
